/** @file guideOwnersOf: which guide region owns each 1x pixel (parts far to near, cut to silhouettes). */
import type { Image } from '@zilo/pose3d-image-io/types/image';
import { screenToTexel } from './screenToTexel';
import type { GuideRegion, RestPlacement } from './sheetTypes';

/** A silhouette covering less than this share of its tile is ignored (it describes another bone; the tile is used). */
const MIN_COVER = 0.25;

/** The mask when it is the tile's size and covers enough of it, else null (the whole tile counts). */
function usableMask(mask: Image | undefined, size: [number, number]): Image | null {
  if (mask?.width !== size[0] || mask.height !== size[1]) return null;
  let covered = 0;
  for (let i = 3; i < mask.data.length; i += 4) if ((mask.data[i] ?? 0) > 0) covered += 1;
  return covered >= MIN_COVER * size[0] * size[1] ? mask : null;
}

/** Writes region `r` into `owners` over every pixel centre inside its quad (and its mask). */
function paintRegion(owners: Int16Array, width: number, region: GuideRegion, r: number, mask: Image | null): void {
  const { quad } = region.part;
  const xs = quad.map((q) => q[0]);
  const ys = quad.map((q) => q[1]);
  const height = owners.length / width;
  for (let y = Math.max(0, Math.floor(Math.min(...ys))); y < Math.min(height, Math.ceil(Math.max(...ys))); y++) {
    for (let x = Math.max(0, Math.floor(Math.min(...xs))); x < Math.min(width, Math.ceil(Math.max(...xs))); x++) {
      const t = screenToTexel(region.part, x + 0.5, y + 0.5);
      if (t === null || (mask !== null && (mask.data[(t[1] * mask.width + t[0]) * 4 + 3] ?? 0) === 0)) continue;
      owners[y * width + x] = r;
    }
  }
}

/**
 * `placement.width * placement.height` region indices (-1 = none). Regions paint in order (far to near, so the nearer
 * one owns an overlap); a region is its tile's quad, cut to `masks.get(slot)` (the server's silhouette) when that mask
 * has the tile's size and covers at least a quarter of it.
 */
export function guideOwnersOf(placement: RestPlacement, regions: readonly GuideRegion[], masks: ReadonlyMap<string, Image>): Int16Array {
  const owners = new Int16Array(placement.width * placement.height).fill(-1);
  regions.forEach((region, r) => { paintRegion(owners, placement.width, region, r, usableMask(masks.get(region.part.slot), region.part.size)); });
  return owners;
}
